import { memo, useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { v4 as uuid } from 'uuid';
import { Button } from '../../components/atoms/Button';
import { Textarea } from '../../components/atoms/Textarea';
import { InlineTitleEditor } from '../../components/atoms/InlineTitleEditor';
import { cn } from '../../lib/utils';
import type { TemplateSection, TemplateSubheader } from './types';

/**
 * Template review overlay — matches web UI's RankableCardList experience:
 *   - Drag-reorder sections AND subsections with @dnd-kit/sortable
 *   - Collapsible rich AI metadata (description, must_include, guidance,
 *     depth/complexity badges) behind a per-section chevron
 *   - Inline rename, delete, add for both levels
 *   - "+ Add section" button at the bottom opens an inline form (not modal)
 *
 * Perf:
 *   - Memoised SectionCard — only re-renders when its own section or the
 *     section's expanded flag changes.
 *   - Drag events are local state; we only fire `onChange` on drop/commit.
 */

interface Props {
  template: TemplateSection[];
  onChange: (t: TemplateSection[]) => void;
  onNext: () => void;
  onCancel: () => void;
  busy?: boolean;
}

export function DraftTemplateOverlay({
  template,
  onChange,
  onNext,
  onCancel,
  busy,
}: Props): JSX.Element {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [addingSection, setAddingSection] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const sectionIds = useMemo(() => template.map((s) => s.id), [template]);

  const handleSectionReorder = useCallback(
    (evt: DragEndEvent) => {
      const { active, over } = evt;
      if (!over || active.id === over.id) return;
      const oldIndex = template.findIndex((s) => s.id === active.id);
      const newIndex = template.findIndex((s) => s.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      onChange(arrayMove(template, oldIndex, newIndex));
    },
    [template, onChange],
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const updateSection = useCallback(
    (id: string, patch: Partial<TemplateSection>) => {
      onChange(template.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    },
    [template, onChange],
  );

  const deleteSection = useCallback(
    (id: string) => {
      onChange(template.filter((s) => s.id !== id));
    },
    [template, onChange],
  );

  const addSection = useCallback(
    (header: string, description?: string) => {
      const next: TemplateSection = {
        id: uuid(),
        number: String(template.length + 1),
        header,
        description: description || undefined,
        subheaders: [],
      };
      onChange([...template, next]);
    },
    [template, onChange],
  );

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-background">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <div>
          <div className="text-sm font-semibold">Review template</div>
          <div className="text-[11px] text-muted-foreground">
            Reorder, edit, add or remove sections — then continue.
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 scrollbar-thin space-y-1.5">
        {template.length === 0 && !addingSection ? (
          <div className="text-xs text-muted-foreground italic py-10 text-center">
            No sections returned. Cancel and try a different brief.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleSectionReorder}
          >
            <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
              {template.map((section, idx) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  index={idx}
                  expanded={expanded.has(section.id)}
                  onToggleExpanded={() => toggleExpanded(section.id)}
                  onUpdate={(patch) => updateSection(section.id, patch)}
                  onDelete={() => deleteSection(section.id)}
                  busy={busy}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}

        {addingSection ? (
          <AddSectionForm
            onAdd={(h, d) => {
              addSection(h, d);
              setAddingSection(false);
            }}
            onCancel={() => setAddingSection(false)}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-2 gap-1.5"
            onClick={() => setAddingSection(true)}
            disabled={busy}
          >
            <Plus className="w-3.5 h-3.5" />
            Add section
          </Button>
        )}
      </div>

      <div className="border-t border-border px-3 py-2">
        <Button
          variant="default"
          size="sm"
          className="w-full"
          onClick={onNext}
          disabled={busy || template.length === 0}
        >
          Next — generate questions
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section card — memoised so typing into one card doesn't re-render others
// ---------------------------------------------------------------------------

interface SectionCardProps {
  section: TemplateSection;
  index: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onUpdate: (patch: Partial<TemplateSection>) => void;
  onDelete: () => void;
  busy?: boolean;
}

const SectionCard = memo(function SectionCardImpl({
  section,
  index,
  expanded,
  onToggleExpanded,
  onUpdate,
  onDelete,
  busy,
}: SectionCardProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const [editingTitle, setEditingTitle] = useState(false);
  const [addingSub, setAddingSub] = useState(false);

  const number = section.number ?? String(index + 1);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-md border border-border bg-background"
    >
      <div className="flex items-start gap-1 px-2 py-2">
        <button
          type="button"
          className="mt-0.5 p-0.5 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
          title="Drag to reorder"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <span className="mt-0.5 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-primary/10 text-primary text-[10px] font-semibold flex-shrink-0">
          {number}
        </span>

        <div className="flex-1 min-w-0">
          {editingTitle ? (
            <InlineTitleEditor
              initial={section.header}
              onSave={(v) => {
                onUpdate({ header: v });
                setEditingTitle(false);
              }}
              onCancel={() => setEditingTitle(false)}
            />
          ) : (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="w-full text-left flex items-start gap-1"
              title={section.description || section.header}
            >
              {expanded ? (
                <ChevronDown className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
              )}
              <span className="text-sm font-medium text-foreground leading-snug">
                {section.header}
              </span>
            </button>
          )}
          {!editingTitle && section.description && !expanded && (
            <div className="text-[11px] text-muted-foreground truncate mt-0.5 ml-4">
              {section.description}
            </div>
          )}
        </div>

        <div className="flex items-center">
          <IconBtn title="Rename" disabled={busy} onClick={() => setEditingTitle(true)}>
            <Pencil className="w-3 h-3" />
          </IconBtn>
          <IconBtn title="Delete" disabled={busy} onClick={onDelete}>
            <Trash2 className="w-3 h-3" />
          </IconBtn>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border/60 bg-muted/20 px-3 py-2 space-y-2">
          {section.description && (
            <p className="text-[11px] text-foreground/80 leading-relaxed">
              {section.description}
            </p>
          )}
          <div className="flex flex-wrap gap-1">
            {section.expectedDepth && (
              <Badge tone="neutral" label={`Depth: ${section.expectedDepth}`} />
            )}
            {section.complexity && (
              <Badge
                tone={section.complexity === 'complex' ? 'warn' : 'neutral'}
                label={`Complexity: ${section.complexity}`}
              />
            )}
          </div>
          {section.mustInclude && section.mustInclude.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">
                Must include
              </div>
              <div className="flex flex-wrap gap-1">
                {section.mustInclude.map((item, i) => (
                  <Chip key={i} label={item} />
                ))}
              </div>
            </div>
          )}
          {section.guidance && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">
                Guidance
              </div>
              <p className="text-[11px] text-foreground/80 leading-relaxed whitespace-pre-wrap">
                {section.guidance}
              </p>
            </div>
          )}

          {/* Subheaders */}
          <SubheaderList
            parentIndex={index}
            subheaders={section.subheaders}
            onChange={(next) => onUpdate({ subheaders: next })}
            busy={busy}
          />

          {addingSub ? (
            <AddSubheaderForm
              onAdd={(t, d) => {
                onUpdate({
                  subheaders: [
                    ...section.subheaders,
                    {
                      id: uuid(),
                      number: `${number}.${section.subheaders.length + 1}`,
                      title: t,
                      description: d || undefined,
                    },
                  ],
                });
                setAddingSub(false);
              }}
              onCancel={() => setAddingSub(false)}
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-[11px] gap-1"
              disabled={busy}
              onClick={() => setAddingSub(true)}
            >
              <Plus className="w-3 h-3" />
              Add subsection
            </Button>
          )}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Subheader list (also draggable within its parent section)
// ---------------------------------------------------------------------------

interface SubheaderListProps {
  parentIndex: number;
  subheaders: TemplateSubheader[];
  onChange: (next: TemplateSubheader[]) => void;
  busy?: boolean;
}

const SubheaderList = memo(function SubheaderListImpl({
  parentIndex: _parentIndex,
  subheaders,
  onChange,
  busy,
}: SubheaderListProps): JSX.Element | null {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const ids = useMemo(() => subheaders.map((s) => s.id), [subheaders]);

  if (subheaders.length === 0) return null;

  const handleReorder = (evt: DragEndEvent) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIdx = subheaders.findIndex((s) => s.id === active.id);
    const newIdx = subheaders.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onChange(arrayMove(subheaders, oldIdx, newIdx));
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">
        Subsections
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {subheaders.map((sh) => (
              <SubheaderRow
                key={sh.id}
                sub={sh}
                onUpdate={(patch) =>
                  onChange(subheaders.map((s) => (s.id === sh.id ? { ...s, ...patch } : s)))
                }
                onDelete={() => onChange(subheaders.filter((s) => s.id !== sh.id))}
                busy={busy}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
});

interface SubheaderRowProps {
  sub: TemplateSubheader;
  onUpdate: (patch: Partial<TemplateSubheader>) => void;
  onDelete: () => void;
  busy?: boolean;
}

function SubheaderRow({ sub, onUpdate, onDelete, busy }: SubheaderRowProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sub.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const [editing, setEditing] = useState(false);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-1 px-2 py-1.5 rounded border border-border/70 bg-background"
    >
      <button
        type="button"
        className="p-0.5 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-3 h-3" />
      </button>
      {sub.number && (
        <span className="text-[10px] text-muted-foreground font-mono mt-0.5 flex-shrink-0">
          {sub.number}
        </span>
      )}
      <div className="flex-1 min-w-0">
        {editing ? (
          <InlineTitleEditor
            initial={sub.title}
            onSave={(v) => {
              onUpdate({ title: v });
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
            compact
          />
        ) : (
          <div className="text-[12px] text-foreground leading-snug truncate" title={sub.description || sub.title}>
            {sub.title}
          </div>
        )}
      </div>
      <div className="flex items-center">
        <IconBtn title="Rename" disabled={busy} onClick={() => setEditing(true)}>
          <Pencil className="w-2.5 h-2.5" />
        </IconBtn>
        <IconBtn title="Delete" disabled={busy} onClick={onDelete}>
          <Trash2 className="w-2.5 h-2.5" />
        </IconBtn>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function IconBtn({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={cn(
        'inline-flex items-center justify-center w-6 h-6 rounded text-muted-foreground',
        'hover:text-foreground hover:bg-muted/70 disabled:opacity-40 disabled:cursor-not-allowed',
      )}
    >
      {children}
    </button>
  );
}

function Badge({
  label,
  tone,
}: {
  label: string;
  tone: 'neutral' | 'warn';
}): JSX.Element {
  const cls = tone === 'warn'
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-muted text-foreground/80 border-border';
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[10px]', cls)}>
      {label}
    </span>
  );
}

function Chip({ label }: { label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded bg-background border border-border px-1.5 py-0.5 text-[10px] text-foreground/80">
      {label}
    </span>
  );
}

function AddSectionForm({
  onAdd,
  onCancel,
}: {
  onAdd: (header: string, description?: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [header, setHeader] = useState('');
  const [description, setDescription] = useState('');

  return (
    <div className="rounded-md border border-primary/40 bg-primary/5 p-2 space-y-1.5 mt-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-foreground">New section</span>
        <IconBtn title="Cancel" onClick={onCancel}>
          <X className="w-3 h-3" />
        </IconBtn>
      </div>
      <input
        autoFocus
        type="text"
        value={header}
        onChange={(e) => setHeader(e.target.value)}
        placeholder="Section title"
        className="w-full bg-background border border-border rounded px-2 py-1 text-sm"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && header.trim()) {
            e.preventDefault();
            onAdd(header.trim(), description.trim() || undefined);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        maxHeight={80}
        className="text-[12px]"
      />
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!header.trim()}
          onClick={() => onAdd(header.trim(), description.trim() || undefined)}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function AddSubheaderForm({
  onAdd,
  onCancel,
}: {
  onAdd: (title: string, description?: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  return (
    <div className="rounded border border-primary/40 bg-primary/5 p-2 space-y-1.5">
      <input
        autoFocus
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Subsection title"
        className="w-full bg-background border border-border rounded px-2 py-1 text-[12px]"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && title.trim()) {
            e.preventDefault();
            onAdd(title.trim(), description.trim() || undefined);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        maxHeight={60}
        className="text-[11px]"
      />
      <div className="flex justify-end gap-1">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!title.trim()}
          onClick={() => onAdd(title.trim(), description.trim() || undefined)}
        >
          Add
        </Button>
      </div>
    </div>
  );
}
